import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../../contexts/ModalContext';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import LoadingState from '../../../../../shared-components/LoadingState';
import { getSessions } from '../../api/history';
import { ScorableItem } from '../../api/scorable-items';
import HistoryTable from './HistoryTable';

const BASE_PATH = 'PAGES.RESULTS.SESSION_HISTORY_MODAL';

export default function HistoryModal({
  scorableItem,
  memberId,
  groupId,
  moduleId,
  isAssignment,
  id,
}: {
  scorableItem: ScorableItem;
  memberId: string;
  groupId: string;
  moduleId: string;
  isAssignment: boolean;
  id: string;
}) {
  const [sessionsData, setSessionsData] = useState<Awaited<ReturnType<typeof getSessions>>>();
  const { closeModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    setIsLoading(true);

    getSessions({
      exerciseType: scorableItem.type,
      groupId,
      isAssignment,
      materialId: scorableItem.id,
      memberId,
      moduleId,
    })
      .then(data => {
        setSessionsData(data);
      })
      .catch(error => {
        handleError(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [scorableItem, groupId, isAssignment, memberId, moduleId, handleError]);

  function handleClose() {
    closeModal(id);
  }

  return (
    <Dialog
      id={id}
      modal
      className="history-modal"
      onClose={handleClose}
      size="full"
      aria-label={t(`${BASE_PATH}.DIALOG_LABEL`)}
    >
      <Dialog.Header
        title={t(`${BASE_PATH}.DIALOG_TITLE`, {
          memberName: sessionsData?.memberName,
          scorableItemName: scorableItem.name,
        })}
        onClose={handleClose}
        aria-label={t('ACTIONS.CLOSE')}
      ></Dialog.Header>
      <Dialog.Body>
        <div>
          {isLoading || !sessionsData ? (
            <LoadingState variant="table" aria-label="loading" columns={5} rows={11} />
          ) : (
            <HistoryTable sessionsData={sessionsData} scorableItem={scorableItem} />
          )}
        </div>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          'aria-label': t('ACTIONS.CLOSE'),
          role: 'group',
          children: (
            <Button prominence="neutral" onClick={handleClose}>
              {t('ACTIONS.CLOSE')}
            </Button>
          ),
        }}
      />
    </Dialog>
  );
}
